/**
 * InsightsFieldSelector - Type Definitions
 *
 * Types for the Insights field selector component.
 * Handles InsightsAnalysis entities with 3-level hierarchy:
 * analysis → JTBD category → individual insight item.
 */

import type { BaseTreeNode, BaseFieldSelectorProps } from '../../shared/types';

/**
 * JTBD categories used in aggregated insights
 */
export const JTBD_CATEGORIES = [
  'coreJobs',
  'contexts',
  'fears',
  'bigJobs',
  'subtasks',
  'competitors',
  'habits',
] as const;

export type JtbdCategory = typeof JTBD_CATEGORIES[number];

/**
 * JTBD category display labels
 */
export const JTBD_CATEGORY_LABELS: Record<JtbdCategory, string> = {
  coreJobs: 'Core Jobs',
  contexts: 'Contexts',
  fears: 'Fears',
  bigJobs: 'Big Jobs',
  subtasks: 'Subtasks',
  competitors: 'Competitors',
  habits: 'Habits',
};

/**
 * Aggregated insight item structure
 * (matches InsightsAnalysisHandler output)
 */
export interface AggregatedInsightItem {
  id: string;
  category?: string;
  canonicalDescription: string;
  cumulativeWeight?: number;
  frequency?: number;
  avgWeight?: number;
  questionText?: string;
  sources?: any[];
  [key: string]: any;
}

/**
 * InsightsAnalysis document (minimal subset for picker)
 */
export interface InsightsAnalysisForPicker {
  id: string;
  name?: string;
  status?: 'pending' | 'running' | 'completed' | 'failed';
  summary?: string;
  mode?: 'b2c' | 'b2b' | 'mixed';
  aggregatedInsights?: Record<string, AggregatedInsightItem[]>;
  audienceBuyingMechanics?: any[];
  audienceContentBriefs?: any[];
  audienceReadinessProfiles?: any[];
  mixedModeWarning?: boolean;
  selectedActorRefs?: Array<{ actorId: string; actorName: string; audienceId: string; audienceName: string }>;
  [key: string]: any;
}

/**
 * Node types in the insights tree (3 levels)
 */
export type InsightsNodeType = 'analysis' | 'category' | 'item';

/**
 * Insights tree node with metadata
 */
export interface InsightsTreeNode extends BaseTreeNode {
  nodeType: InsightsNodeType;

  /** Analysis ID this node belongs to */
  analysisId: string;

  /** JTBD category (for category and item nodes) */
  category?: JtbdCategory;

  /** Insight item ID (for item nodes) */
  insightId?: string;
}

/**
 * Hydrated insights data (resolved from paths)
 */
export interface InsightsHydratedData {
  /**
   * W13 (IS1, fix round 2): the UNFILTERED universe — every thesis the loaded
   * analyses hold, ticked or not.
   *
   * `analyses` below is `pathsToJson`'s output, i.e. only the items the operator
   * TICKED. Measuring «how many theses are available» against it answers
   * «available === selected» by construction, so the create/edit dialog cannot
   * tell a narrowed selection from a complete one. This field is what a
   * selection is measured against; it carries ids and categories only.
   */
  available?: Array<{
    id: string;
    insights: Array<{ id: string; category: string }>;
  }>;
  /** The TICKED subset, hydrated for the AI payload. */
  analyses: Array<{
    id: string;
    name?: string;
    summary?: string;
    mode?: 'b2c' | 'b2b' | 'mixed';
    aggregatedInsights: Record<string, AggregatedInsightItem[]>;
    audienceBuyingMechanics?: any[];
    audienceContentBriefs?: any[];
    audienceReadinessProfiles?: any[];
    mixedModeWarning?: boolean;
  }>;
}

/**
 * Props for InsightsFieldSelector component
 */
export interface InsightsFieldSelectorProps extends BaseFieldSelectorProps {
  /** Optional: filter analyses by task (via selectedActorRefs → audience → taskContext.taskId) */
  taskFilter?: string;

  /** Optional callback for hydrated data (resolved analyses with selected insights) */
  onHydratedDataChange?: (data: InsightsHydratedData) => void;

  /**
   * Phase F: emit the pinned analysis (the single selected analysis) + the task ids it spans
   * (perTaskPackages keys → fallback aggregatedInsights[*].sources[].taskId). Used to constrain the
   * Tasks picker to the analysis's tasks. analysisId is null when nothing is selected.
   */
  onPinnedAnalysisChange?: (info: { analysisId: string | null; taskIds: string[] }) => void;
}
